import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import Loader from './Components/Loader';
import MapMapbox from './Components/MapMapbox';
import Controls from './Components/MapControls';
import Info from './Components/Info';
import Filter from './Components/Filter';
import Logos from './Components/Logos';
import Header from './Components/Header';
import HoverTooltip from './Components/HoverTooltip';

import { LAYERS, DEFAULT_LANG } from './Utilities/Config';
import { getTranslation, gsheetToJSON } from './Utilities/Helpers';
import { MapProvider } from './Services/MapContext';

import './App.css';

export default function App() {
	const [layersLoaded, setLayersLoaded] = useState(false);

	// Language
	const [language, setLanguage] = useState(null);
	const [availableLanguages, setAvailableLanguages] = useState(null);

	// Query search parameters
	const [searchParams] = useSearchParams();

	// Windows/forms
	const [loaderVisible, setLoaderVisible] = useState(true);
	const [filterVisible, setFilterVisible] = useState(true);
	const [infoVisible, setInfoVisible] = useState(false);
	
	const handleFilterVisibility = () => { setFilterVisible((prevState) => !prevState); }
	const handleInfoVisibility = () => { setInfoVisible((prevState) => !prevState); }

	// List of layers
	const [layersVisible, setLayersVisible] = useState(LAYERS);

	// Hover tooltip
	const [hoverTooltipVisible, setHoverTooltipVisible] = useState(false);
	const [mousePosition, setMousePosition] = useState({ left: 0, top: 0 });
	const [tooltipContent, setTooltipContent] = useState({id: undefined, content: undefined});
	const handleMouseMove = (ev) => { setMousePosition(ev); };

	// Wait for data to load
	useEffect(() => {
		if (!language || !layersLoaded) return;
		setLoaderVisible(false);
	}, [language, layersLoaded]);

	// On component render
	useEffect(() => {
		const fetchLanguage = async () => {
			const lang = searchParams.get('lang') || DEFAULT_LANG;
			const response = await getTranslation();
			const languageJSON = gsheetToJSON(response);
			
			// generate a list of available languages
			const langArray = () => {
				let object = {};
				Object.entries(languageJSON.filter(val => val.param === 'language:name')[0]).forEach((el) => {
					if (el[0] !== 'param') object[el[0].replace('translation_','')] = el[1];
				});
				return object;
			}

			// generate text hashtable
			let translation = languageJSON.reduce((acc, val) => {
				if (val.param) acc[val.param] = val[`translation_${lang}`];
				return acc;
			}, {});

			setLanguage(translation);
			setAvailableLanguages(langArray());

			return translation;
		}

		const fetchData = async () => {
			// eslint-disable-next-line
			const lang = searchParams.get('lang') || DEFAULT_LANG;
			const translation = await fetchLanguage();
			setLanguage(translation);
		}

		fetchData();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<div className="App">
			<Loader
				show={loaderVisible}
			/>
			<Header
				language={language}
				availableLanguages={availableLanguages}
				searchParams={searchParams}
			/>
			<MapProvider>
				<Controls
					onToggleFilter={handleFilterVisibility}
					onToggleInfo={handleInfoVisibility}
				/>
				<MapMapbox
					layersVisible={layersVisible}
					setLayersLoaded={setLayersLoaded}
					onMouseMove={(ev)=> handleMouseMove(ev)}
					setTooltipContent={setTooltipContent}
					setHoverTooltipVisible={setHoverTooltipVisible}
				>
					<HoverTooltip
						data={tooltipContent}
						mousePosition={mousePosition}
						hoverTooltipVisible={hoverTooltipVisible}
						setHoverTooltipVisible={setHoverTooltipVisible}
						language={language}
					/>
				</MapMapbox>
			</MapProvider>
			<Filter
				show={filterVisible}
				onToggle={handleFilterVisibility}
				layersVisible={layersVisible}
				setLayersVisible={setLayersVisible}
				language={language}
			/>
			<Info
				language={language}
				show={infoVisible}
				onToggle={handleInfoVisibility}
			/>
			<Logos/>
		</div>
	);
}
